import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";

/**
 * La firma del FRC-019, compartida por las dos pantallas que la
 * necesitan: Solicitar (el colaborador al pedir, el líder al autorizar)
 * y Calendario (quien administra, al autorizar).
 *
 * Dos maneras de firmar, a elegir:
 *  - Digital: se traza aquí mismo y viaja como PNG; sale impresa en el
 *    documento sobre su línea.
 *  - En papel: se manda la operación sin trazo, se descarga el FRC-019
 *    con la línea en blanco, y se firma a mano. Viaja el marcador
 *    FISICA, que el documento entiende como "línea vacía".
 *
 * El componente no sabe qué hay detrás: solo emite la firma o la
 * cancelación. Quien lo abre decide qué mandar y qué descargar.
 */
@Component({
  selector: "app-firma-frc",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./firma-frc.component.html",
  styleUrls: ["./firma-frc.component.css"],
})
export class FirmaFrcComponent {
  /** Cambia los textos: quien pide firma su solicitud; quien autoriza, la autorización. */
  @Input() para: "pedir" | "autorizar" = "pedir";

  /** De quién es la solicitud, para que el líder sepa qué está firmando. */
  @Input() nombre?: string;

  /**
   * Qué formato se está firmando. El modal nació sólo para vacaciones y
   * decía "FRC-019" y "las vacaciones" también cuando lo abría permisos,
   * que fue lo que se vio en pantalla al firmar un FRC-017. Los textos
   * salen de aquí; por omisión sigue siendo el de vacaciones, así que
   * quien ya lo usaba no cambia en nada.
   */
  @Input() documento: "FRC-019" | "FRC-017" | "EVAL-IA" | "HOMOLOGACION-IA" = "FRC-019";

  /**
   * Sin «Descargar para firmar». Lo usan Evaluaciones IA Solser y
   * Homologación de Modelos IA: lo que se firma ahí no genera formato que
   * imprimir. Por omisión, las dos maneras.
   */
  @Input() soloDigital = false;

  /** Si viene, sustituye la explicación: qué se firma y qué pasa al firmar. */
  @Input() leyenda?: string;

  /**
   * Lo que además del trazo pide quien abre el cuadro para habilitar el
   * botón; Homologación proyecta aquí su campo de PIN y pasa si ya se
   * escribió. Por omisión, solo el trazo: quien ya lo usaba no cambia.
   */
  @Input() listo = true;

  /**
   * Mientras quien lo abrió manda la firma. Homologación deja el cuadro
   * abierto hasta que el servidor contesta: con un PIN equivocado se
   * corrige el PIN sin volver a trazar. Sin botones mientras tanto, para
   * no mandar la firma dos veces.
   */
  @Input() ocupado = false;

  /** El PNG de la firma trazada, o "FISICA" si se firmará en papel. */
  @Output() firmada = new EventEmitter<string>();
  @Output() cancelada = new EventEmitter<void>();

  /** El marcador de firma en papel. Lo comparte el backend y el documento. */
  static readonly FISICA = "FISICA";

  modo: "digital" | "fisica" = "digital";
  tieneTrazo = false;

  private trazando = false;

  @ViewChild("lienzo") lienzo?: ElementRef<HTMLCanvasElement>;

  get titulo(): string {
    if (this.esHomologacion) {
      return "Firma el dictamen";
    }
    if (this.esEvaluacion) {
      return "Firma la evaluación";
    }
    return this.para === "pedir" ? "Firma tu solicitud" : "Firma la autorización";
  }

  private get esEvaluacion(): boolean {
    return this.documento === "EVAL-IA";
  }

  private get esHomologacion(): boolean {
    return this.documento === "HOMOLOGACION-IA";
  }

  private get esPermiso(): boolean {
    return this.documento === "FRC-017";
  }

  /** Cómo se llama el formato en los textos. */
  get docNombre(): string {
    return this.esPermiso ? "FRC-017" : "FRC-019";
  }

  /** El formato con su nombre completo, para la tarjeta de descarga. */
  get docTitulo(): string {
    return this.esPermiso ? "FRC-017 Permiso" : "FRC-019 Vacaciones";
  }

  /** "Permiso de Fulano" / "Vacaciones de Fulano". */
  get etiquetaQuien(): string {
    if (this.esHomologacion) {
      return "Homologación de";
    }
    if (this.esEvaluacion) {
      return "Evaluación de";
    }
    return this.esPermiso ? "Permiso de" : "Vacaciones de";
  }

  get explicacion(): string {
    if (this.leyenda) {
      return this.leyenda;
    }
    if (this.esHomologacion) {
      return "Tu firma y tu PIN sellan el dictamen.";
    }
    if (this.esEvaluacion) {
      return "Tu firma sella la evaluación.";
    }
    // Dos frases y no una con sustantivo intercambiable: "el permiso" y
    // "las vacaciones" no concuerdan con el mismo verbo.
    if (this.modo === "fisica") {
      return this.para === "pedir"
        ? "Se envía tu solicitud y se descarga el " + this.docNombre +
          " con la línea de firma en blanco, para que lo firmes a mano."
        : (this.esPermiso ? "Se autoriza el permiso" : "Se autorizan las vacaciones") +
          " y se descarga el " + this.docNombre +
          " con tu línea de firma en blanco, para que lo firmes a mano.";
    }
    return this.para === "pedir"
      ? "Tu firma queda plasmada en el formato " + this.docNombre + " de esta solicitud."
      : "Tu firma sella el " + this.docNombre + " y " +
        (this.esPermiso ? "el permiso queda autorizado" : "las vacaciones quedan autorizadas") + ".";
  }

  /**
   * La tarjeta de «Descargar para firmar». Desde el 30/09/2026 el papel
   * firmado se sube a la solicitud («Subir firmado», app-frc-firmado):
   * antes solo decía que se entregara.
   */
  get textoPapel(): string {
    return this.para === "pedir"
      ? "Se descarga en cuanto confirmes. Fírmalo a mano y, ya firmado, súbelo desde tu solicitud con «Subir firmado»."
      : "Se descarga en cuanto confirmes. Fírmalo a mano; el papel firmado se sube desde la solicitud con «Subir firmado».";
  }

  get textoBoton(): string {
    if (this.ocupado) {
      return "Firmando…";
    }
    if (this.esHomologacion) {
      return "Firmar dictamen";
    }
    if (this.esEvaluacion) {
      return "Firmar y cerrar";
    }
    if (this.modo === "fisica") {
      return this.para === "pedir" ? "Enviar y descargar" : "Autorizar y descargar";
    }
    return this.para === "pedir" ? "Firmar y enviar" : "Firmar y autorizar";
  }

  get puedeConfirmar(): boolean {
    return !this.ocupado && this.listo && (this.modo === "fisica" || this.tieneTrazo);
  }

  cambiaModo(modo: "digital" | "fisica"): void {
    if (modo === this.modo || (this.soloDigital && modo === "fisica")) {
      // Ya está ahí: no hay canvas nuevo que limpiar ni trazo que olvidar.
      return;
    }
    this.modo = modo;
    if (modo === "digital") {
      // El canvas se acaba de crear: limpio y sin trazo.
      this.tieneTrazo = false;
    }
  }

  /* ================= El trazo ================= */

  limpiar(): void {
    if (this.ocupado) {
      return;
    }
    const canvas = this.lienzo?.nativeElement;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    this.tieneTrazo = false;
  }

  empieza(ev: PointerEvent): void {
    ev.preventDefault();
    if (this.ocupado) {
      // Lo que ya se mandó es el trazo de antes: no se le agrega nada.
      return;
    }
    const canvas = this.lienzo?.nativeElement;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) {
      return;
    }
    // Con la captura, el trazo sigue aunque el dedo se salga del recuadro.
    canvas.setPointerCapture(ev.pointerId);
    this.trazando = true;
    const p = this.puntoDe(ev, canvas);
    ctx.strokeStyle = "#16212b";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    // Un toque también es trazo: sin esto, un punto no pintaría nada.
    ctx.lineTo(p.x + 0.1, p.y + 0.1);
    ctx.stroke();
    this.tieneTrazo = true;
  }

  mueve(ev: PointerEvent): void {
    if (!this.trazando) {
      return;
    }
    ev.preventDefault();
    const canvas = this.lienzo?.nativeElement;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) {
      return;
    }
    const p = this.puntoDe(ev, canvas);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  }

  termina(): void {
    this.trazando = false;
  }

  /**
   * Del evento a coordenadas del bitmap. El canvas mide 600x240 por
   * dentro pero se estira al ancho del modal, así que se escala por la
   * proporción real; sin esto el trazo saldría corrido del cursor.
   */
  private puntoDe(ev: PointerEvent, canvas: HTMLCanvasElement): { x: number; y: number } {
    const r = canvas.getBoundingClientRect();
    return {
      x: (ev.clientX - r.left) * (canvas.width / r.width),
      y: (ev.clientY - r.top) * (canvas.height / r.height),
    };
  }

  /* ================= Salida ================= */

  confirmar(): void {
    if (!this.puedeConfirmar) {
      return;
    }
    if (this.modo === "fisica") {
      this.firmada.emit(FirmaFrcComponent.FISICA);
      return;
    }
    const canvas = this.lienzo?.nativeElement;
    if (!canvas || !this.tieneTrazo) {
      return;
    }
    this.firmada.emit(canvas.toDataURL("image/png"));
  }

  cancelar(): void {
    if (this.ocupado) {
      return;
    }
    this.trazando = false;
    this.cancelada.emit();
  }
}
